import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError } from './Api/ApiError';
import { getLoginData } from './Api/Staff';
import { getSystemValue } from './Api/System';
import StoreDetailAdjustment from './components/organisms/StoreDetailAdjustment';
import StoreDetailBusinessHours from './components/organisms/StoreDetailBusinessHours';
import StoreDetailCancel from './components/organisms/StoreDetailCancel';
import StoreDetailEquipment from './components/organisms/StoreDetailEquipment';
import StoreDetailOnlineLesson from './components/organisms/StoreDetailOnlineLesson';
import StoreDetailStudioEdit from './components/organisms/StoreDetailStudioEdit';
import StoreDetailStudioPack from './components/organisms/StoreDetailStudioPack';
import StoreDetailStudioPrice from './components/organisms/StoreDetailStudioPrice';
import StoreDetailStudioTagEdit from './components/organisms/StoreDetailStudioTagEdit';
import AuthConfigPage from './components/pages/AuthConfigPage';
import CustomerPage from './components/pages/CustomerPage';
import DatabaseConfigPage from './components/pages/DatabaseConfigPage';
import HomePage from './components/pages/HomePage';
import InitializePage from './components/pages/InitializePage';
import LessonTeacherPage from './components/pages/LessonTeacherPage';
import NewsPage from './components/pages/NewsPage';
import OnlineLessonPage from './components/pages/OnlineLessonPage';
import SettingPage from './components/pages/SettingPage';
import SigninPage from './components/pages/SigninPage';
import StaffPage from './components/pages/StaffPage';
import StoreDetailPage from './components/pages/StoreDetailPage';
import StorePage from './components/pages/StorePage';
import DashboardTemplate from './components/templates/DashboardTemplate';
import { StoreObjectProvider } from './ObjectStore/StoreObjectStore';
import { ObjectToSystemObject, SystemObjectStore } from './ObjectStore/SystemObjectStore';
import { UserObjectStore } from './ObjectStore/UserObjectStore';

interface RequireAuthProps {
    children: JSX.Element,
    requireDBVersion?: number,
    requireAccessKey?: string
}

interface AccessDenyProps {
    text?: string
}

const AccessDeny = (props?: AccessDenyProps) => {
    return (
        <Box
            p={2}
        >
            <Typography>アクセスできません</Typography>
            {props?.text &&
                <Typography>{props.text}</Typography>
            }
        </Box>
    )
}

const RequireAuth = ({
    children,
    requireDBVersion,
    requireAccessKey
}: RequireAuthProps): JSX.Element => {
    const { userObjectState } = React.useContext(UserObjectStore)
    const { systemObject } = React.useContext(SystemObjectStore)
    const location = useLocation()

    const [view, setView] = React.useState<JSX.Element>()

    // if( requireDBVersion ) {
    //     if( system )
    // }

    React.useEffect(() => {
        if( requireDBVersion ) {
            if(!systemObject.databaseVersion || systemObject.databaseVersion < requireDBVersion) {
                setView(
                    <AccessDeny
                        text={`アクセスにはデータベースのバージョンアップが必要です`}
                    />
                )
                return
            }
        }
        if (userObjectState.stfId) {
            if (requireAccessKey) {
                let keys = requireAccessKey.split(",")
                if (!userObjectState.stfAccessList.some(elem => keys.includes(elem))) {
                    setView(
                        <AccessDeny />
                    )
                }
            }
            setView(children)
        } else {
            setView(
                <Navigate to="/login" state={{ from: location }} replace />
            )
        }
    }, [userObjectState, children, requireDBVersion, systemObject.databaseVersion, location, requireAccessKey])


    return (
        <>{view}</>
    )
}

const App: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar()

    let { userObjectDispatch } = React.useContext(UserObjectStore)
    const { setSystemObject } = React.useContext(SystemObjectStore)

    React.useEffect(() => {
        const controller = new AbortController()
        getLoginData("Administrator", {signal: controller.signal}).then(staffLoginData => {
            userObjectDispatch({ type: "setUser", payload: staffLoginData })
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            }
        })
        return () => {
            controller.abort()
        }
    }, [userObjectDispatch, enqueueSnackbar])

    React.useEffect(() => {
        const controller = new AbortController()
        getSystemValue({
            site: "system_manager"
        }, {signal: controller.signal}).then(datas => {
            setSystemObject(ObjectToSystemObject(datas))
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                console.info(e.message)
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
        return () => {
            controller.abort()
        }
    }, [enqueueSnackbar, setSystemObject])

    return (
        <BrowserRouter basename='/manager'>
            <DashboardTemplate>
                <Routes>
                    <Route path="/initialize" element={<InitializePage />} />
                    <Route path="/login" element={<SigninPage />} />
                    <Route path='/' element={<RequireAuth><HomePage /></RequireAuth>} />
                    <Route path="/setting" element={<RequireAuth requireAccessKey='SystemConfig'><SettingPage /></RequireAuth>} />
                    <Route path="/database" element={<RequireAuth requireAccessKey='SystemConfig'><DatabaseConfigPage /></RequireAuth>} />
                    <Route path="/auth" element={<RequireAuth requireAccessKey='AuthConfig'><AuthConfigPage /></RequireAuth>} />
                    <Route path="/staff" element={<RequireAuth requireAccessKey='StaffAdmin'><StaffPage /></RequireAuth>} />
                    <Route path="/customer" element={<RequireAuth requireAccessKey='CustomerAdmin'><CustomerPage /></RequireAuth>} />
                    <Route path="/news" element={<RequireAuth requireAccessKey='NewsAdmin'><NewsPage /></RequireAuth>} />
                    <Route path='/store'>
                        <Route path="" element={<RequireAuth><StorePage /></RequireAuth>} />
                        <Route path=":id" element={<RequireAuth><StoreObjectProvider><StoreDetailPage /></StoreObjectProvider></RequireAuth>} >
                            <Route path='studio' element={<StoreDetailStudioEdit />}/>
                            <Route path='studiotag' element={<StoreDetailStudioTagEdit />}/>
                            <Route path='onlinelesson' element={<RequireAuth requireDBVersion={11}><StoreDetailOnlineLesson /></RequireAuth>}/>
                            <Route path='businesshours' element={<StoreDetailBusinessHours />}/>
                            <Route path='studioprice' element={<StoreDetailStudioPrice />}/>
                            <Route path='pack' element={<StoreDetailStudioPack />}/>
                            <Route path='equipment' element={<StoreDetailEquipment />}/>
                            <Route path='adjustment' element={<StoreDetailAdjustment />}/>
                            <Route path='cancel' element={<StoreDetailCancel />}/>
                        </Route>
                    </Route>
                    <Route path='/online_lesson'>
                        <Route path="" element={<RequireAuth requireAccessKey='CustomerAdmin' requireDBVersion={11}><OnlineLessonPage /></RequireAuth>} />
                        <Route path=":teacher_id" element={<RequireAuth requireAccessKey='CustomerAdmin' requireDBVersion={11}><LessonTeacherPage /></RequireAuth>} />
                    </Route>
                </Routes>
            </DashboardTemplate>
        </BrowserRouter>
    );
}

export default App;
